// WebAuthn (Face ID / Touch ID) utilities

// Check if WebAuthn is supported
export const isWebAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined;
};

// Check if platform authenticator (Face ID/Touch ID) is available
export const isPlatformAuthenticatorAvailable = async () => {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// Convert base64url to ArrayBuffer
const base64urlToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Convert ArrayBuffer to base64url
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

// Register new credential (enable Face ID)
export const registerCredential = async (options) => {
  const publicKeyOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rp: options.rp,
    user: {
      id: base64urlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout,
    excludeCredentials: (options.excludeCredentials || []).map(c => ({
      type: c.type,
      id: base64urlToBuffer(c.id),
    })),
    authenticatorSelection: options.authenticatorSelection,
    attestation: options.attestation,
  };

  const credential = await navigator.credentials.create({
    publicKey: publicKeyOptions,
  });

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64url(credential.response.attestationObject),
    },
    type: credential.type,
  };
};

// Authenticate with credential (use Face ID to login)
export const authenticateCredential = async (options) => {
  const publicKeyOptions = {
    challenge: base64urlToBuffer(options.challenge),
    timeout: options.timeout,
    rpId: options.rpId,
    allowCredentials: (options.allowCredentials || []).map(c => ({
      type: c.type,
      id: base64urlToBuffer(c.id),
    })),
    userVerification: options.userVerification,
  };

  const credential = await navigator.credentials.get({
    publicKey: publicKeyOptions,
  });

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      authenticatorData: bufferToBase64url(credential.response.authenticatorData),
      signature: bufferToBase64url(credential.response.signature),
    },
    type: credential.type,
  };
};

// Store email for quick login
export const storeEmailForBiometric = (email) => {
  localStorage.setItem('biometric_email', email);
};

// Get stored email
export const getStoredEmail = () => {
  return localStorage.getItem('biometric_email');
};

// Clear stored email
export const clearStoredEmail = () => {
  localStorage.removeItem('biometric_email');
};
